import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import {
  Box,
  Columns,
  Column,
  Inline,
  Bleed,
  Divider,
  Button,
  ResponsiveRender
} from '@machobear/design-system'
import { Formik, Form } from 'formik'
import { add } from '@/models/plans/apis'
import usePayments from '@/hooks/use-payments'
import { DialogEventHandler } from '@/types'
import { breakpoints } from '@/styles'
import queryClient from '@/components/query-client'
import useMediaQuery from '@/hooks/use-media-query'
import { usePaymentValidation, initialValues } from '@/hooks/use-payment-validation'
import { AddingPaymentMethod } from './types'
import PaymentMethod from './payment-method'
import PurchaseSummary from './purchase-summary'
import { usePaymentContext } from './payment-context'

type Props = {
  closeModal: DialogEventHandler
  queryKeys?: string[]
}

const CheckoutModalContent = ({ closeModal, queryKeys }: Props) => {
  const { t } = useTranslation()

  const { purchase, coupon } = usePaymentContext()
  const { paymentInfo, loading } = usePayments()
  const hasPaymentMethods = paymentInfo && paymentInfo?.length !== 0

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)

  const addMethod = useMutation({
    mutationFn: (values: AddingPaymentMethod) => add(values),
    onSuccess: async response => {
      queryClient.invalidateQueries({ queryKey: queryKeys })

      try {
        await purchase(response[0].payment_method_id, coupon)
        closeModal()
      } catch (error: any) {
        console.log(error)
      }
    },
    onError: error => {
      throw error
    }
  })

  const handlePurchase = async (values: AddingPaymentMethod) => {
    if (hasPaymentMethods) {
      await purchase(paymentInfo[0].payment_method_id, coupon)
      closeModal()
    } else {
      await addMethod.mutateAsync({
        ...values,
        card_number: values.card_number.replace(/\s/g, ''),
        expiration_date: values.expiration_date.replace('/', '')
      })
    }
  }

  const validationSchema = usePaymentValidation()
  const emptySchema = Yup.object().shape({})

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={hasPaymentMethods ? emptySchema : validationSchema}
      onSubmit={handlePurchase}
    >
      {({ values, isSubmitting }) => {
        return (
          <Form>
            <Bleed horizontal={isMobile ? 'medium' : 'large'}>
              <Divider />
              <Columns space="none" collapseBelow="tablet">
                <Column width="2/5">
                  <PurchaseSummary
                    state={hasPaymentMethods ? paymentInfo[0]?.state : values.state}
                    country={hasPaymentMethods ? paymentInfo[0]?.country : values.country}
                  />
                </Column>
                <Column width="3/5">
                  <Box display="flex" flexDirection="column" justifyContent="spaceBetween">
                    <PaymentMethod isLoading={loading} paymentInfo={paymentInfo} />
                  </Box>
                </Column>
              </Columns>
              <Divider />
            </Bleed>
            <Box paddingTop="large">
              <ResponsiveRender above="mobile">
                <Inline space="xsmall" align="right">
                  <Button variant="transparent" disabled={isSubmitting} onClick={closeModal}>
                    {t('common.back', 'Back')}
                  </Button>
                  <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                    {isSubmitting
                      ? t('common.submitting2', 'Submitting')
                      : t('common.submit', 'Submit')}
                  </Button>
                </Inline>
              </ResponsiveRender>
              <ResponsiveRender below="tablet">
                <Box display="flex" alignItems="center" gap="xsmall">
                  <Button variant="transparent" disabled={isSubmitting} onClick={closeModal}>
                    {t('common.back', 'Back')}
                  </Button>
                  <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                    {isSubmitting
                      ? t('common.submitting2', 'Submitting')
                      : t('common.submit', 'Submit')}
                  </Button>
                </Box>
              </ResponsiveRender>
            </Box>
          </Form>
        )
      }}
    </Formik>
  )
}

export default CheckoutModalContent
